import { Alert, Box, Button, Typography } from '@mui/material';
import { FileSyncStatus } from '@/common/models/enums';
import type { useProjectChangeScan } from '../hooks/useProjectChangeScan';

export default function ProjectChangesBanner({
  scan,
  canUpload,
  onCreate,
  busy,
}: {
  scan: Omit<ReturnType<typeof useProjectChangeScan>, 'scanForTransfer'>;
  canUpload: boolean;
  onCreate: () => void;
  busy: boolean;
}) {
  if (!scan.available || busy || scan.transferActive) return null;
  const statuses = scan.snapshot?.statuses ?? [];
  const localChanges = statuses.filter((file) =>
    [FileSyncStatus.NEW, FileSyncStatus.NEWER, FileSyncStatus.DELETED].includes(
      file.status
    )
  ).length;
  const needsReview = statuses.some(
    (file) =>
      file.status === FileSyncStatus.BEHIND ||
      file.status === FileSyncStatus.UNKNOWN
  );
  if (!scan.error && !localChanges && !needsReview)
    return (
      <Typography variant="caption" color="text.secondary" role="status">
        {scan.scanning
          ? 'Checking local changes…'
          : scan.snapshot
            ? 'No unuploaded changes · checked in the background about every minute'
            : 'Background change checks enabled'}
      </Typography>
    );
  return (
    <Alert
      severity={scan.error || needsReview ? 'warning' : 'info'}
      sx={{ '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {scan.error
              ? 'Could not check local changes'
              : needsReview
                ? 'File differences need review'
                : 'Cloud is out of date'}
          </Typography>
          <Typography variant="body2">
            {scan.error ||
              (needsReview
                ? 'Some files are unreadable or behind the cloud version. Review Sync before uploading.'
                : `${localChanges} local file ${localChanges === 1 ? 'change has' : 'changes have'} not been uploaded. Create a version when you’re ready.`)}
          </Typography>
          {scan.snapshot && (
            <Typography variant="caption" color="text.secondary">
              Compared with latest cloud version {scan.snapshot.version}
              {scan.scanning ? ' · Checking again…' : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            color="inherit"
            disabled={scan.scanning}
            onClick={() => void scan.scan()}
          >
            Check again
          </Button>
          {!scan.error && !needsReview && canUpload && (
            <Button size="small" variant="outlined" onClick={onCreate}>
              Create version
            </Button>
          )}
        </Box>
      </Box>
    </Alert>
  );
}
